import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import PageShell from "components/patch/PageShell";

const setupMenus = [
  {
    title: "Staff List",
    description: "View nominal roll, search employees, and review active and inactive records.",
    icon: <PeopleIcon fontSize="medium" />,
    route: "/hr/employee-management/stafflist",
    badge: "Registry",
  },
  {
    title: "Staff Profiles",
    description: "Open and manage individual staff profiles, employment details, and history.",
    icon: <PersonIcon fontSize="medium" />,
    route: "/hr/employees/profile",
    badge: "Profiles",
  },
  {
    title: "Edit Staff",
    description: "Update staff details and maintain accurate payroll and employment records.",
    icon: <EditIcon fontSize="medium" />,
    route: "/hr/employee-management/add-edit",
    badge: "Update",
  },
];

export default function SetupMenu() {
  const navigate = useNavigate();

  return (
    <PageShell
      title="Employee Management"
      subtitle="Manage employee records, staff profiles, and payroll-related updates from one workspace."
    >
      <MDBox mt={1}>
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3 },
            borderRadius: "24px",
            background: "#fff",
            border: "1px solid rgba(200, 30, 30, 0.08)",
            boxShadow: "0 12px 28px rgba(16, 40, 79, 0.04)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "0.74rem",
                  fontWeight: 800,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "#c81e1e",
                  mb: 1,
                }}
              >
                Employee Management
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "1.5rem", md: "1.85rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  color: "#10284f",
                  mb: 1,
                }}
              >
                Staff workspace
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.95rem",
                  lineHeight: 1.75,
                  color: "#62708a",
                  maxWidth: 760,
                }}
              >
                Open the main employee management modules for staff list review, profile search, and
                record updates.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                label="Staff Registry"
                sx={{
                  height: 34,
                  fontWeight: 700,
                  color: "#b42318",
                  background: "#fdf0f0",
                  borderRadius: "999px",
                }}
              />
              <Chip
                label="Profile Access"
                sx={{
                  height: 34,
                  fontWeight: 700,
                  color: "#1d4ed8",
                  background: "#eff6ff",
                  borderRadius: "999px",
                }}
              />
              <Chip
                label="Record Updates"
                sx={{
                  height: 34,
                  fontWeight: 700,
                  color: "#027a48",
                  background: "#ecfdf3",
                  borderRadius: "999px",
                }}
              />
            </Stack>
          </Stack>
        </Box>

        <Grid container spacing={2.5} alignItems="stretch">
          {setupMenus.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              <Card
                sx={{
                  height: "100%",
                  minHeight: 230,
                  borderRadius: "22px",
                  border: "1px solid rgba(19, 34, 56, 0.08)",
                  background: "#fff",
                  boxShadow: "0 10px 24px rgba(16, 40, 79, 0.04)",
                  overflow: "hidden",
                  transition:
                    "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 18px 36px rgba(16, 40, 79, 0.08)",
                    borderColor: "rgba(200, 30, 30, 0.12)",
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(item.route)}
                  sx={{ height: "100%", display: "flex", alignItems: "stretch" }}
                >
                  <CardContent
                    sx={{
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ mb: 2.5 }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "18px",
                          background: "linear-gradient(135deg, #d61f1f 0%, #b91c1c 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          boxShadow: "0 10px 20px rgba(200, 30, 30, 0.16)",
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Chip
                        label={item.badge}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          color: "#64748b",
                          background: "#f8fafc",
                          borderRadius: "999px",
                        }}
                      />
                    </Stack>

                    <Typography
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        color: "#10284f",
                        mb: 1.25,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.92rem",
                        lineHeight: 1.75,
                        color: "#62708a",
                        mb: 2.5,
                        flexGrow: 1,
                      }}
                    >
                      {item.description}
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      sx={{
                        color: "#c81e1e",
                        fontWeight: 700,
                        fontSize: "0.9rem",
                      }}
                    >
                      <span>Open module</span>
                      <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    </PageShell>
  );
}
